import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/AdminLayout';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiSave, FiLock, FiSmartphone, FiCheckCircle, FiMail, FiGlobe, FiBell, FiMessageSquare, FiAlertTriangle, FiClock } = FiIcons;

const Settings = () => {
  const [twilioConfig, setTwilioConfig] = useState({ sid: '', token: '', phone: '' });
  const [notificationEmail, setNotificationEmail] = useState('');
  
  // New Notification Preferences State
  const [adminPrefs, setAdminPrefs] = useState({
    newLeads: true,
    weeklyReport: false,
    systemErrors: true,
    smsAlerts: false
  });

  const [customerDefaults, setCustomerDefaults] = useState({
    defaultSms: true,
    defaultEmail: true,
    reminderTime: '09:00'
  });

  const [isSaved, setIsSaved] = useState(false);

  // Load Settings
  useEffect(() => {
    const storedSettings = localStorage.getItem('ramirez_settings');
    if (storedSettings) {
      const parsed = JSON.parse(storedSettings);
      setTwilioConfig(parsed.twilio || { sid: '', token: '', phone: '' });
      setNotificationEmail(parsed.email || 'cleanairtruckcheck@gmail.com');
      setAdminPrefs(parsed.adminPrefs || { newLeads: true, weeklyReport: false, systemErrors: true, smsAlerts: false });
      setCustomerDefaults(parsed.customerDefaults || { defaultSms: true, defaultEmail: true, reminderTime: '09:00' });
    } else {
      // Defaults
      setTwilioConfig({ sid: 'AC...', token: '...', phone: '+19092842738' });
      setNotificationEmail('cleanairtruckcheck@gmail.com');
    }
  }, []);

  const handleSave = (e) => {
    e.preventDefault();
    // Save to Local Storage
    const settings = {
      twilio: twilioConfig,
      email: notificationEmail,
      adminPrefs,
      customerDefaults
    };
    localStorage.setItem('ramirez_settings', JSON.stringify(settings));
    setIsSaved(true);
    setTimeout(() => setIsSaved(false), 3000);
  };

  const toggleAdminPref = (key) => {
    setAdminPrefs(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const toggleCustomerDefault = (key) => {
    setCustomerDefaults(prev => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <AdminLayout>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-white mb-2">System Settings</h1>
        <p className="text-gray-400">Configure global application settings, integrations, and notification rules.</p>
      </div>

      <form onSubmit={handleSave} className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        
        {/* Left Column: Notification Preferences */}
        <div className="space-y-8">
          
          {/* Admin Alerts */}
          <div className="bg-gray-800 border border-gray-700 rounded-xl p-6 shadow-lg">
            <div className="flex items-center gap-3 mb-6 pb-4 border-b border-gray-700">
              <div className="p-3 bg-yellow-600/20 rounded-lg">
                <SafeIcon icon={FiBell} className="text-2xl text-yellow-500" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-white">Admin Notification Preferences</h2>
                <p className="text-xs text-gray-400">Control when YOU get notified</p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-gray-700/50 rounded-lg border border-gray-600">
                <div className="flex items-center gap-3">
                  <SafeIcon icon={FiMail} className="text-blue-400" />
                  <div>
                    <h4 className="text-white font-medium text-sm">New Lead Alerts</h4>
                    <p className="text-xs text-gray-400">Receive email when a quote is requested</p>
                  </div>
                </div>
                <button 
                  type="button"
                  onClick={() => toggleAdminPref('newLeads')}
                  className={`w-12 h-6 rounded-full transition-colors relative ${adminPrefs.newLeads ? 'bg-green-500' : 'bg-gray-600'}`}
                >
                  <span className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${adminPrefs.newLeads ? 'translate-x-6' : 'translate-x-0'}`} />
                </button>
              </div>

              <div className="flex items-center justify-between p-4 bg-gray-700/50 rounded-lg border border-gray-600">
                <div className="flex items-center gap-3">
                  <SafeIcon icon={FiAlertTriangle} className="text-red-400" />
                  <div>
                    <h4 className="text-white font-medium text-sm">System Critical Alerts</h4>
                    <p className="text-xs text-gray-400">Get notified of API/Database errors</p>
                  </div>
                </div>
                <button 
                  type="button"
                  onClick={() => toggleAdminPref('systemErrors')}
                  className={`w-12 h-6 rounded-full transition-colors relative ${adminPrefs.systemErrors ? 'bg-green-500' : 'bg-gray-600'}`}
                >
                  <span className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${adminPrefs.systemErrors ? 'translate-x-6' : 'translate-x-0'}`} />
                </button>
              </div>

              <div className="flex items-center justify-between p-4 bg-gray-700/50 rounded-lg border border-gray-600">
                <div className="flex items-center gap-3">
                  <SafeIcon icon={FiClock} className="text-purple-400" />
                  <div>
                    <h4 className="text-white font-medium text-sm">Weekly Digest</h4>
                    <p className="text-xs text-gray-400">Summary of fleet compliance stats</p>
                  </div>
                </div>
                <button 
                  type="button"
                  onClick={() => toggleAdminPref('weeklyReport')}
                  className={`w-12 h-6 rounded-full transition-colors relative ${adminPrefs.weeklyReport ? 'bg-green-500' : 'bg-gray-600'}`}
                >
                  <span className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${adminPrefs.weeklyReport ? 'translate-x-6' : 'translate-x-0'}`} />
                </button>
              </div>
            </div>
          </div>

          {/* Customer Defaults */}
          <div className="bg-gray-800 border border-gray-700 rounded-xl p-6 shadow-lg">
            <div className="flex items-center gap-3 mb-6 pb-4 border-b border-gray-700">
              <div className="p-3 bg-blue-600/20 rounded-lg">
                <SafeIcon icon={FiMessageSquare} className="text-2xl text-blue-500" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-white">Subscriber Default Settings</h2>
                <p className="text-xs text-gray-400">Default preferences for new clients</p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div 
                  onClick={() => toggleCustomerDefault('defaultSms')}
                  className={`p-4 rounded-lg border cursor-pointer transition-all ${customerDefaults.defaultSms ? 'bg-blue-900/20 border-blue-500' : 'bg-gray-700/30 border-gray-600'}`}
                >
                  <div className="flex justify-between items-center mb-2">
                    <SafeIcon icon={FiSmartphone} className={customerDefaults.defaultSms ? 'text-blue-400' : 'text-gray-500'} />
                    <div className={`w-3 h-3 rounded-full ${customerDefaults.defaultSms ? 'bg-blue-500' : 'bg-gray-600'}`}></div>
                  </div>
                  <p className="text-sm font-bold text-white">SMS Enabled</p>
                  <p className="text-xs text-gray-400">Text reminders on by default</p>
                </div>

                <div 
                  onClick={() => toggleCustomerDefault('defaultEmail')}
                  className={`p-4 rounded-lg border cursor-pointer transition-all ${customerDefaults.defaultEmail ? 'bg-purple-900/20 border-purple-500' : 'bg-gray-700/30 border-gray-600'}`}
                >
                  <div className="flex justify-between items-center mb-2">
                    <SafeIcon icon={FiMail} className={customerDefaults.defaultEmail ? 'text-purple-400' : 'text-gray-500'} />
                    <div className={`w-3 h-3 rounded-full ${customerDefaults.defaultEmail ? 'bg-purple-500' : 'bg-gray-600'}`}></div>
                  </div>
                  <p className="text-sm font-bold text-white">Email Enabled</p>
                  <p className="text-xs text-gray-400">Email reminders on by default</p>
                </div>
              </div>

              <div>
                <label className="block text-gray-300 text-sm font-medium mb-2">Automated Reminder Time</label>
                <input 
                  type="time" 
                  value={customerDefaults.reminderTime}
                  onChange={(e) => setCustomerDefaults({...customerDefaults, reminderTime: e.target.value})}
                  className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
                />
                <p className="text-xs text-gray-500 mt-1">Time of day to send automated alerts (PST)</p>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column: Integrations */}
        <div className="space-y-8">
          
          {/* General Email Config */}
          <div className="bg-gray-800 border border-gray-700 rounded-xl p-6 shadow-lg">
            <div className="flex items-center gap-3 mb-6 pb-4 border-b border-gray-700">
              <div className="p-3 bg-gray-700 rounded-lg">
                <SafeIcon icon={FiGlobe} className="text-2xl text-white" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-white">Global Configuration</h2>
                <p className="text-xs text-gray-400">General app settings</p>
              </div>
            </div>
            
            <div className="space-y-4">
               <div>
                  <label className="block text-white font-medium mb-2 text-sm">Admin Contact Email</label>
                  <div className="relative">
                    <SafeIcon icon={FiMail} className="absolute left-3 top-3 text-gray-400" />
                    <input 
                      type="email" 
                      value={notificationEmail} 
                      onChange={(e) => setNotificationEmail(e.target.value)}
                      className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg pl-10 pr-4 py-2.5 focus:ring-2 focus:ring-blue-500 outline-none text-sm" 
                      placeholder="admin@example.com" 
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-2">Used for 'Reply-To' in automated emails.</p>
               </div>
            </div>
          </div>

          {/* Twilio Integration */}
          <div className="bg-gray-800 border border-gray-700 rounded-xl p-6 shadow-lg">
            <div className="flex items-center gap-3 mb-6 pb-4 border-b border-gray-700">
              <div className="p-3 bg-red-600/20 rounded-lg">
                <SafeIcon icon={FiSmartphone} className="text-2xl text-red-500" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-white">Twilio Integration</h2>
                <p className="text-xs text-gray-400">SMS Gateway Configuration</p>
              </div>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-gray-300 text-sm font-medium mb-2">Account SID</label>
                <div className="relative">
                  <SafeIcon icon={FiLock} className="absolute left-3 top-3.5 text-gray-500" />
                  <input type="text" value={twilioConfig.sid} onChange={(e) => setTwilioConfig({...twilioConfig, sid: e.target.value})} className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg pl-10 pr-4 py-3 focus:ring-2 focus:ring-blue-500 outline-none font-mono text-sm" placeholder="AC..." />
                </div>
              </div>
              <div>
                <label className="block text-gray-300 text-sm font-medium mb-2">Auth Token</label>
                <div className="relative">
                  <SafeIcon icon={FiLock} className="absolute left-3 top-3.5 text-gray-500" />
                  <input type="password" value={twilioConfig.token} onChange={(e) => setTwilioConfig({...twilioConfig, token: e.target.value})} className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg pl-10 pr-4 py-3 focus:ring-2 focus:ring-blue-500 outline-none font-mono text-sm" placeholder="• • • • • • • •" />
                </div>
              </div>
              <div>
                <label className="block text-gray-300 text-sm font-medium mb-2">From Number (Twilio)</label>
                <div className="relative">
                  <SafeIcon icon={FiSmartphone} className="absolute left-3 top-3.5 text-gray-500" />
                  <input type="text" value={twilioConfig.phone} onChange={(e) => setTwilioConfig({...twilioConfig, phone: e.target.value})} className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg pl-10 pr-4 py-3 focus:ring-2 focus:ring-blue-500 outline-none font-mono text-sm" placeholder="+1909..." />
                </div>
              </div>
            </div>
          </div>

          <button 
            type="submit" 
            className="w-full bg-blue-600 text-white font-bold py-4 rounded-xl hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 shadow-lg shadow-blue-900/50"
          >
            {isSaved ? (
              <>
                <SafeIcon icon={FiCheckCircle} />
                <span>All Settings Saved</span>
              </>
            ) : (
              <>
                <SafeIcon icon={FiSave} />
                <span>Save Configuration</span>
              </>
            )}
          </button>

        </div>
      </form>
    </AdminLayout>
  );
};

export default Settings;